import { FC } from 'react';
import { Link, useLocation } from 'react-router';
import { Button, Text } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import { internalAdminRoutes } from '@/router/internalAdminRoutes';
import { toClassNames } from '@/utils';
import { isProductionEnv } from '@/utils/appEnvironment';

type SidebarItem = {
  icon: IconName;
  to: string;
  label: string;
};

/**
 * Either a nav item or a group caption. Sections must be exhaustive — a single
 * captioned group in a flat list reads as "everything below belongs to it", so
 * once any caption exists, every group gets one. Items above the first caption
 * are the unambiguous top level.
 */
type SidebarEntry = SidebarItem | { section: string };

const isSection = (entry: SidebarEntry): entry is { section: string } => 'section' in entry;

/**
 * A nav item stays highlighted on its section's child routes (e.g.
 * `/vocab-library/:senseId/edit` keeps Vocab Library lit), so exact match OR
 * direct prefix. The `to + '/'` guard keeps `/items` from matching an
 * unrelated `/items-x`.
 */
export const isSidebarItemActive = (pathname: string, to: string): boolean => {
  return pathname === to || pathname.startsWith(`${to}/`);
};

type SideBarItemProps = {
  item: SidebarItem;
  active: boolean;
};

const SideBarItem: FC<SideBarItemProps> = ({ item, active }) => {
  return (
    <Button
      as={Link}
      to={item.to}
      variant="unstyled"
      aria-current={active ? 'page' : undefined}
      className={toClassNames(
        'flex flex-col items-center gap-y-1 rounded-none py-4 text-inherit hover:bg-gray-600/50',
        active && 'bg-gray-600'
      )}
    >
      <Icon icon={item.icon} />
      <Text>{item.label}</Text>
    </Button>
  );
};

const SectionCaption: FC<{ label: string }> = ({ label }) => {
  return (
    <div
      className="mt-5 px-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-200"
      aria-hidden="true"
    >
      {label}
    </div>
  );
};

export const InternalAdminSidebar = () => {
  const location = useLocation();
  const sidebarEntries: SidebarEntry[] = [
    // Top level — the directory: organizations and their people.
    {
      icon: IconName.School,
      to: internalAdminRoutes.Schools.getRoute(),
      label: 'Schools',
    },
    {
      icon: IconName.UserGroup,
      to: internalAdminRoutes.Users.getRoute(),
      label: 'Users',
    },
    // Everything admins curate or author for learners.
    { section: 'Content' },
    {
      icon: IconName.Book,
      to: internalAdminRoutes.VocabLibrary.getRoute(),
      label: 'Vocab Library',
    },
    {
      icon: IconName.Download,
      to: internalAdminRoutes.ActivityBank.getRoute(),
      label: 'Activity Bank',
    },
    // Prod-only (LX-1062): the backend refuses asset uploads off production,
    // so non-production builds get no entry point at all — not a disabled one.
    ...(isProductionEnv()
      ? [
          {
            icon: IconName.CloudUpload,
            to: internalAdminRoutes.AssetUpload.getRoute(),
            label: 'Asset Upload',
          },
        ]
      : []),
    {
      icon: IconName.PlayCircle,
      to: internalAdminRoutes.ActivityPreview.getRoute(),
      label: 'Activity Preview',
    },
    {
      icon: IconName.MagicWand,
      to: internalAdminRoutes.ContentBuilder.getRoute(),
      label: 'Content Builder',
    },
    {
      icon: IconName.GridFilled,
      to: internalAdminRoutes.Items.getRoute(),
      label: 'Items',
    },
    {
      icon: IconName.CardsArcFilled,
      to: internalAdminRoutes.AiLessonGenerator.getRoute(),
      label: 'AI Lesson Gen',
    },
    {
      icon: IconName.ClipBoardCheck,
      to: internalAdminRoutes.LessonBlueprints.getRoute(),
      label: 'Sheet Lessons',
    },
    // Moving data in and out of the system.
    { section: 'Data' },
    {
      icon: IconName.Export,
      to: internalAdminRoutes.DataExports.getRoute(),
      label: 'Export Data',
    },
    {
      icon: IconName.Upload,
      to: internalAdminRoutes.BulkImport.getRoute(),
      label: 'Bulk Import',
    },
    // Platform configuration.
    { section: 'System' },
    {
      icon: IconName.Toggles,
      to: internalAdminRoutes.FeatureFlags.getRoute(),
      label: 'Feature Flags',
    },
  ];
  return (
    <div className="flex flex-col py-8 text-white">
      {sidebarEntries.map((entry) =>
        isSection(entry) ? (
          <SectionCaption key={`section-${entry.section}`} label={entry.section} />
        ) : (
          <SideBarItem key={entry.to} item={entry} active={isSidebarItemActive(location.pathname, entry.to)} />
        )
      )}
    </div>
  );
};
